import SvgIcon from '@/pages/home/components/SvgIcon';
import React, { Fragment, useMemo, useRef, useState } from 'react';
import { Bubble, Card, Icon, Justify, Radio, Segment, Select } from 'tea-component';

import './index.scss';
import { useIntl } from 'umi';
import NoDataCommon from '../NoDataCommon';
import LoadingCommon from '../LoadingCommon';
import { buriedCardBtnPoint } from '@op/operationLog/buriedPoint';
import CommonBubble from '@home/components/CommonBubble';

export type operation = {
  symbol?: string; // 操作标识
  tip?: string; // 悬浮上去的提示
  handleClick?: (item: operation) => void;
  icon?: string; // 操作图标
  element?: any; // 可以自定义放内容
  text?: string; // 可以自定义放内容
  placement?: string; // 气泡位置
  placementOffset?: Array<number>; // 气泡位置
  rotate?: number;
  isImg?: boolean;
};

type dropdown = {
  value: string; // 值
  text: string; // 文字
  handleClick?: (item: operation) => void;
};

type filterType = {
  text: string | JSX.Element;
  value: string;
  children?: {
    text: string;
    value: string;
  }[];
};

export type CardCommonType = {
  title?: string | JSX.Element; // 标题，字符串和 JSX 都可以

  subTitle?: string | JSX.Element; // 副标题，字符串和 JSX 都可以

  children?: string | JSX.Element | Array<JSX.Element> | any; // 插入内容
  hasTitleArrow?: boolean; // 如果需要左边右边箭头可以点击去的 true ，默认false， 可以不传
  handleTitleOnClick?: () => void; // 标题点击事件
  cardHeaderElement?: JSX.Element;

  hasDownload?: boolean; // 是否展示下载  默认flase不展示
  handleDownloadOnClick?: () => void; // 下载 点击事件
  downloadIcon?: string;
  downLoadTips?: string;

  subFilterClassName?: string;
  subFilterBeforeElement?: JSX.Element;
  operations?: operation[];
  alwaysShowOperations?: boolean;
  operationInfo?: [string, (value: any) => void];
  defaultOperation?: string; // 默认点亮的操作
  handleOperationsOnClick?: (item: operation) => void; // 统一的操作点击事件

  className?: string; // 添加额外的类名

  filters?: filterType[]; // 筛选，要支持两级
  filterClassName?: string;
  filterInfo?: [string, (value: any) => void];
  handleFilterOnChange?: (value: string) => void;
  handleSubFilterOnChange?: (value: string) => void;

  contentCard?: boolean; // 是否为内容卡片，有另外一套样式
  dropdownsFilterBeforeElement?: JSX.Element;
  dropdowns?: dropdown[]; // 下拉操作
  defaultDropDown?: string; // 默认的下拉操作选项
  dropdownFooter?: string | JSX.Element;
  handleDropdownOnChange?: (item: dropdown) => void; // 统一的下拉点击事件

  bodyStyle?: React.CSSProperties;

  keepFiltersSpace?: boolean; // 是否保留筛选器的位置
  keepFiltersSpaceHeight?: number; // 保留筛选器的位置的高度
  keepSubFiltersSpace?: boolean; // 是否保留子筛选器
  keepSubFiltersSpaceHeight?: number; // 是否子筛选器的高度

  // loading 和 空白占位 统一处理

  loading?: boolean; // 是否在加载中
  loadingHeight?: number | string; // 加载状态高度
  loadingExt?: any; // loading 额外元素
  loadingText?: string;

  empty?: boolean;
  emptyHeight?: number | string;
  hasEmptyImg?: boolean;
  hasEmptyTitle?: boolean;
  hasEmptyDesc?: boolean;

  emptyTitleText?: string;
  emptyDescText?: string | React.ReactNode;
  emptyDescElement?: any;
  logBtnId?: string;
  extraInfo?: string | { [key: string]: any };
  [key: string]: any;
  otherSelectProps?: any;
  renderSelectsAfterOperations?: boolean; // 是否在操作后面渲染下拉选择框
  hoverNoShadow?: boolean;
  downloadLoading?: boolean;
};

/**
 * TODO: 其他系统使用
 * Tea组件卡片组件封装
 * @param props
 * @returns
 */
export default function CardCommon(props: CardCommonType) {
  const { formatMessage } = useIntl();

  const {
    title,
    subTitle,
    hasTitleArrow,
    handleTitleOnClick,
    cardHeaderElement = null,

    operations = [],
    alwaysShowOperations = false,
    operationInfo,
    defaultOperation,
    handleOperationsOnClick,

    downloadIcon = 'download',
    hasDownload,
    handleDownloadOnClick,
    downLoadTips = 'Download',

    children,
    className,

    filters = [],
    filterInfo,
    filterClassName,
    handleFilterOnChange,
    handleSubFilterOnChange,

    contentCard = false,

    subFilterClassName,
    subFilterBeforeElement = null,
    dropdownsFilterBeforeElement = null,
    dropdowns = [],
    defaultDropDown,
    dropdownFooter,
    handleDropdownOnChange,

    bodyStyle,

    keepFiltersSpace = false,
    keepSubFiltersSpace = false,
    keepFiltersSpaceHeight = 52,
    keepSubFiltersSpaceHeight = 32,

    loading = false,
    loadingHeight = '100%',
    empty = false,
    emptyHeight = '100%',
    hasEmptyImg = true,
    hasEmptyTitle = true,
    hasEmptyDesc = true,

    emptyTitleText = 'No data yet',
    emptyDescText = 'No data under current filter selections.',
    emptyDescElement = null,
    logBtnId = '',
    extraInfo,
    otherSelectProps,
    loadingExt,
    loadingText,
    renderSelectsAfterOperations = false,
    hoverNoShadow = false,
    downloadLoading = false,
    ...otherProps
  } = props;
  const [dropdown, setDropdown] = useState(
    defaultDropDown || (dropdowns?.length > 0 && dropdowns[0]?.value) || '',
  );

  const [operation, setOperation] =
    operationInfo ||
    useState(
      defaultOperation ||
      (operations?.length > 0 && operations[0]?.symbol) ||
      '',
    );

  const defaultFilter = filters?.length > 0 && filters[0];
  const [filter, setFilter] =
    filterInfo || useState((defaultFilter && defaultFilter.value) || '');

  const [subFilter, setSubFilter] = useState(
    (defaultFilter && defaultFilter.children?.[0]?.value) || '',
  );

  // 任意一个有 subfilters 就为 true
  // const hasSubFilters = filters?.some(
  //   (item) =>
  //     item.children && Array.isArray(item.children) && item.children.length > 0,
  // );

  const [subFilters, setSubFilters] = useState<
    { text: string; value: string }[]
  >([]);

  const findListItem = (list: any[], value: string) =>
    list.filter((item) => item.value === value)?.[0] || '';

  const renderOperations = () => {
    return operations?.map((item: operation, index: number) => {
      if (item.element)
        return <Fragment key={item.symbol}>{item.element}</Fragment>;
      if (item.text)
        return (
          <span
            key={item.symbol}
            className="text-info"
            onClick={() => {
              item.symbol &&
                buriedCardBtnPoint(logBtnId, item.symbol, extraInfo);
              handleOperationsOnClick?.(item);
            }}
          >
            {item.text}
          </span>
        );

      let tips = null;
      if (item.tip) {
        tips =
          typeof item.tip === 'string'
            ? formatMessage({ id: item.tip })
            : item.tip;
      }

      return (
        <CommonBubble
          content={tips}
          dark
          closeOnScroll
          key={item.symbol}
          arrowPointAtCenter={false}
          placement={item.placement as any}
        >
          <i
            className={`${item.symbol && item.symbol === operation
              ? 'card-slot-action'
              : 'card-slot-item'
              } ${alwaysShowOperations ? 'dbo-show' : ''}`}
            onClick={() => {
              item.symbol && setOperation(item.symbol);
              item.symbol &&
                buriedCardBtnPoint(logBtnId, item.symbol, extraInfo);
              handleOperationsOnClick?.(item);
              item.handleClick?.(item);
            }}
          >
            {item.icon &&
              (item.isImg ? (
                <img
                  style={{ transform: `rotate(${item.rotate || 0}deg)` }}
                  className="card-slot-charicon"
                  src={item.icon}
                ></img>
              ) : (
                <SvgIcon
                  type={item.icon}
                  style={{ transform: `rotate(${item.rotate || 0}deg)` }}
                  className="card-slot-charicon"
                />
              ))}
          </i>
        </CommonBubble>
      );
    });
  };

  const root = useRef<HTMLDivElement>(null);
  const renderSelects = () => {
    return (
      Array.isArray(dropdowns) &&
      dropdowns.length > 0 && (
        <span ref={root} className="dbo-sentiment-select-wrapper">
          <Select
            appearance="default"
            options={dropdowns}
            popupContainer={() => root.current}
            value={dropdown}
            onChange={(value) => {
              setDropdown(value);
              const item = findListItem(dropdowns, value);
              if (item) {
                handleDropdownOnChange?.(item);
                item.handleClick?.(item);
              }
            }}
            overlayClassName="dbo-select-footer"
            footer={<span style={{ fontSize: 14 }}>{dropdownFooter}</span>}
            className="dbo-select-hover"
            {...otherSelectProps}
          />
        </span>
      )
    );
  };

  const renderSelectsWrapper = useMemo(() => {
    if (!renderSelectsAfterOperations) {
      return (
        <>
          {renderSelects()}
          {renderOperations()}
        </>
      );
    }
    return (
      <>
        {renderOperations()}
        {renderSelects()}
      </>
    );
  }, [renderSelectsAfterOperations, renderSelects, renderOperations]);

  const RenderCard = (
    <Card
      className={`card-common card-slot ${className || ''} ${hoverNoShadow ? 'no-hover-shadow' : ''
        }`}
      {...otherProps}
    >
      {cardHeaderElement && <Card.Header>{cardHeaderElement}</Card.Header>}
      <Card.Body
        style={{ ...bodyStyle }}
        className={title === undefined ? 'tea-card__body--notitle' : ''}
        title={
          title && (
            <div
              className={
                hasTitleArrow ? 'card-slot-title-hover' : 'card-slot-title'
              }
              onClick={() => {
                buriedCardBtnPoint(logBtnId, 'title', extraInfo);
                handleTitleOnClick?.();
              }}
            >
              <span>{title}</span>
              {hasTitleArrow && (
                <SvgIcon type="arrow-right" className="card-slot-icon" />
              )}
            </div>
          )
        }
        operation={operations?.length > 0 || dropdowns?.length > 0 || hasDownload || !!dropdownsFilterBeforeElement ? (
          <div className={`card-slot-flex ${alwaysShowOperations ? 'card-slot-show' : ''}`} >
            {dropdownsFilterBeforeElement}
            {(operations?.length > 0 || dropdowns?.length > 0) && (
              <>{renderSelectsWrapper}</>
            )}
            {hasDownload && (
              <Bubble dark content={formatMessage({ id: downLoadTips })}>
                <i
                  className="card-slot-char"
                  onClick={() => {
                    buriedCardBtnPoint(logBtnId, 'download', extraInfo);
                    handleDownloadOnClick?.();
                  }}
                >
                  {
                    downloadLoading ? (
                      <Icon type={'loading'} className="card-slot-charicon" />
                    ) :
                      <SvgIcon
                        type={downloadIcon}
                        className="card-slot-charicon"
                      />
                  }
                </i>
              </Bubble>
            )}
          </div>
        ) : (
          <></>
        )
        }
      >
        <>
          {filters?.length > 0 ? (
            <Segment
              className={`card-slot-segment ${filterClassName}`}
              value={filter.toString()}
              onChange={(value) => {
                setSubFilter('');
                setSubFilters([]);
                setFilter(value);
                handleFilterOnChange?.(value);
                const curFilter = findListItem(filters, value);
                if (curFilter?.children) {
                  setSubFilter(curFilter.children[0]?.value);
                  handleSubFilterOnChange?.(curFilter.children[0]?.value);
                  setSubFilters(curFilter.children);
                }
              }}
              options={filters}
            />
          ) : keepFiltersSpace ? (
            <div
              style={{
                height: keepFiltersSpaceHeight || 60,
              }}
            ></div>
          ) : null}

          <div className={`dbo-filter-box ${subFilterClassName}`}>
            {subFilterBeforeElement}
            {filters?.length > 0 && subFilters?.length > 0 ? (
              <Radio.Group
                value={subFilter}
                onChange={(value) => {
                  setSubFilter(value);
                  handleSubFilterOnChange?.(value);
                }}
              >
                {subFilters.map((item) => (
                  <Radio
                    name={item.value}
                    key={`${filter}-${item.value}-${item.text}`}
                  >
                    {formatMessage({ id: item.text })}
                  </Radio>
                ))}
              </Radio.Group>
            ) : keepSubFiltersSpace ? (
              <div
                style={{
                  height: keepSubFiltersSpaceHeight || 23,
                }}
              ></div>
            ) : null}
          </div>

          {loading ? (
            <LoadingCommon
              status="loading"
              loadingExt={loadingExt}
              loadingText={loadingText}
              style={{ height: loadingHeight }}
            ></LoadingCommon>
          ) : empty ? (
            <NoDataCommon
              hasDesc={hasEmptyDesc}
              hasImg={hasEmptyImg}
              hasTitle={hasEmptyTitle}
              style={{
                height: emptyHeight,
              }}
              emptyTitleText={emptyTitleText}
              emptyDescText={emptyDescText}
              emptyDescElement={emptyDescElement}
            ></NoDataCommon>
          ) : (
            children
          )}
        </>
      </Card.Body>
    </Card>
  );

  const RenderContentCard = (
    <div
      className={`card-common card-common--content-card ${className || ''}`}
      style={bodyStyle}
      {...otherProps}
    >
      {cardHeaderElement && <div>{cardHeaderElement}</div>}
      {(title || operations?.length > 0 || dropdowns?.length > 0 || dropdownsFilterBeforeElement || hasDownload) && (
        <Justify
          left={
            title && (
              <div
                className={`dbo-cardcommon-subtitle ${hasTitleArrow ? 'dbo-cardcommon-subtitle--link' : ''
                  }`}
                onClick={() => handleTitleOnClick?.()}
              >
                <span>{title}</span>
                {hasTitleArrow && (
                  <SvgIcon type="arrow-right" className="secondary-icon" />
                )}
              </div>
            )
          }
          right={
            <>
              {dropdownsFilterBeforeElement}
              {renderSelectsWrapper}
              {hasDownload && (
                <Bubble
                  closeOnScroll
                  dark
                  content={formatMessage({ id: downLoadTips })}
                >
                  <i
                    className="card-slot-char"
                    onClick={() => {
                      buriedCardBtnPoint(logBtnId, 'download', extraInfo);
                      handleDownloadOnClick?.();
                    }}
                  >
                    {
                      downloadLoading ? (
                        <Icon type={'loading'} className="card-slot-charicon" />
                      ) :
                        <SvgIcon
                          type={downloadIcon}
                          className="card-slot-charicon"
                        />
                    }
                  </i>
                </Bubble>
              )}
            </>
          }
        />
      )}

      {subTitle && <div className="echarts-tip-text">{subTitle}</div>}

      {loading ? (
        <LoadingCommon
          status="loading"
          loadingText={loadingText}
          loadingExt={loadingExt}
          style={{
            height: loadingHeight,
          }}
        ></LoadingCommon>
      ) : empty ? (
        <NoDataCommon
          hasDesc={hasEmptyDesc}
          hasImg={hasEmptyImg}
          hasTitle={hasEmptyTitle}
          style={{
            height: emptyHeight,
          }}
          emptyTitleText={emptyTitleText}
          emptyDescText={emptyDescText}
          emptyDescElement={emptyDescElement}
        ></NoDataCommon>
      ) : (
        children
      )}
    </div>
  );

  return contentCard ? RenderContentCard : RenderCard;
}
